import { logSwallowed } from '@/lib/errors';
// ============================================================================
// EmojiPicker.tsx — панель эмоций (SRP: вставка эмодзи в поле ввода).
// ВИЗУАЛ — SVG-картинки из «Картинок» (библиотека reactions, SSoT);
// ВСТАВЛЯЕТСЯ в текст — эмодзи-символ (name картинки), чтобы текст
// оставался обычным читаемым текстом. Кнопка-триггер — lucide Smile.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { Smile } from 'lucide-react';
import { ensureReactionPictures, REACTION_PICTURE_ORDER, knownReactionCodes, reactionChar, reactionLabel, reactionUrl, subscribeReactionPictures } from '@/lib/reactionPictures';

export function EmojiPicker({ onPick, className }: { onPick: (emoji: string) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const [rxVersion, setRxVersion] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const un = subscribeReactionPictures(() => setRxVersion((v) => v + 1));
    ensureReactionPictures().catch((e) => { logSwallowed(e, 'components/ui/EmojiPicker.tsx:19'); return undefined; });
    return un;
  }, []);

  // Закрытие по клику ВНЕ панели и по Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Только реакции с эмодзи-символом (вставка в текст); порядок канонический + дополнения
  const codes = [...new Set([...REACTION_PICTURE_ORDER, ...knownReactionCodes()])].filter((c) => reactionChar(c));

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className || ''}`}>
      <button
        type="button"
        className="h-8 w-8 rounded hover:bg-muted/40 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen((v) => !v)}
        title="Эмоции"
        aria-label="Эмоции"
      >
        <Smile className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-56 rounded-lg border bg-popover shadow-md p-1.5 grid grid-cols-6 gap-0.5 z-10" data-rx-version={rxVersion}>
          {codes.map((code) => {
            const url = reactionUrl(code);
            return (
              <button
                key={code}
                type="button"
                title={reactionLabel(code)}
                className="h-7 w-7 rounded flex items-center justify-center hover:bg-muted/40 transition-colors"
                onClick={() => {
                  const ch = reactionChar(code);
                  if (ch) onPick(ch);
                  setOpen(false);
                }}
              >
                {url ? (
                  <img src={url} alt={reactionLabel(code)} draggable={false} className="h-[22px] w-[22px] object-contain" />
                ) : (
                  <span className="text-base leading-none">{reactionChar(code)}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
